/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { X, Mail, User, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import userService from '../api/userService';
import EditableField from './EditableField';
import { useToast } from './Toast';
import { useAuth } from './AuthContext';
import Modal from './Modal';

export default function ProfileModal({ isOpen, onClose }) {
  const toast = useToast();
  const { user: authUser, updateUser } = useAuth();
  const [user, setUser] = useState(authUser);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const currentUser = authUser;
    setUser(currentUser);
    setError('');

    const id = currentUser?.id || currentUser?._id || currentUser?.userId;
    if (!id) return;

    let cancelled = false;
    setIsLoading(true);
    userService.getById(id)
      .then((res) => {
        if (cancelled) return;
        const fresh = res.data;
        if (fresh) {
          setUser(fresh);
          updateUser(fresh);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.message || 'Ne mogu da učitam profil.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const name = user?.name || '';
  const email = user?.email || '';

  const saveUserName = async (value) => {
    const id = user?.id || user?._id || user?.userId;
    if (!id) {
      setError('Nedostaje ID korisnika.');
      throw new Error('missing user id');
    }
    if (!value) {
      setError('Ime i prezime ne mogu biti prazni.');
      throw new Error('empty');
    }
    setError('');
    try {
      const res = await userService.update(id, { name: value });
      const fresh = res.data;
      const updated = fresh && (fresh.name || fresh.email) ? fresh : { ...user, name: value };
      setUser(updated);
      updateUser(updated);
      toast.success('Ime sačuvano.');
    } catch (err) {
      const msg = err.response?.data?.message || 'Greška pri izmeni imena.';
      setError(msg);
      toast.error(msg);
      throw err;
    }
  };

  const initials = (() => {
    const source = name || email;
    if (!source) return '';
    if (source.includes('@')) return source[0].toUpperCase();
    return source.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
  })();

  return (
    <Modal isOpen={isOpen} onClose={onClose} label="Moj profil">
      <button
        onClick={onClose}
        aria-label="Zatvori"
        className="absolute top-6 right-6 z-10 p-2 text-sage-400 hover:text-sage-600 hover:bg-sage-50 hover:scale-110 rounded-full transition-all"
      >
        <X size={20} />
      </button>
            <div className="p-8 sm:p-10 max-h-[88dvh] overflow-y-auto custom-scrollbar">

              <div className="flex flex-col items-center mb-6">
                <div className="w-20 h-20 rounded-3xl bg-sage-100 text-sage-800 text-3xl font-bold flex items-center justify-center mb-4 shadow-sm">
                  {initials || <User size={32} />}
                </div>
                <h2 className="text-2xl font-serif font-bold text-earth-900 text-center">
                  {name || 'Moj Profil'}
                </h2>
                {email && <p className="text-sage-600 text-sm">{email}</p>}
                <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sage-50 text-sage-700 text-xs font-bold">
                  <ShieldCheck size={12} />
                  Verifikovan nalog
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-2xl px-4 py-3 mb-4">
                  {error}
                </div>
              )}

              {isLoading && !user && (
                <p className="text-center text-sage-500 text-sm py-6">Učitavanje...</p>
              )}

              <div className="space-y-3">
                <EditableField
                  icon={<User size={16} />}
                  label="Ime i prezime"
                  value={name}
                  onSave={saveUserName}
                />
                {email && (
                  <ProfileRow icon={<Mail size={16} />} label="Email" value={email} />
                )}
              </div>

              <button
                onClick={onClose}
                className="w-full mt-8 py-4 bg-sage-600 text-white rounded-2xl font-bold hover:bg-sage-700 hover:shadow-xl hover:shadow-sage-700/30 transition-all"
              >
                Zatvori
              </button>
            </div>
    </Modal>
  );
}

function ProfileRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-4 p-4 bg-sage-50 rounded-2xl border border-sage-100">
      <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center text-sage-600 shrink-0 shadow-sm">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[11px] uppercase font-bold text-sage-500 tracking-wider">{label}</p>
        <p className="text-sm font-bold text-earth-900 truncate">{value}</p>
      </div>
    </div>
  );
}
